/**
 * SA shared components — video, product card, media swiper, recently viewed, article toc, scroll tabs, anchor scroll.
 */
(function () {
const GlobalAudioContext = {
  activeElement: null,
};

const APILoader = {
  yt: { loading: false, callbacks: [] },
  vimeo: { loading: false, callbacks: [] },
  loadYouTube(cb) {
    if (window.YT && window.YT.Player) return cb();
    this.yt.callbacks.push(cb);
    if (!this.yt.loading) {
      this.yt.loading = true;
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      window.onYouTubeIframeAPIReady = () => {
        this.yt.callbacks.forEach((c) => c());
      };
      document.head.appendChild(script);
    }
  },
  loadVimeo(cb) {
    if (window.Vimeo && window.Vimeo.Player) return cb();
    this.vimeo.callbacks.push(cb);
    if (!this.vimeo.loading) {
      this.vimeo.loading = true;
      const script = document.createElement("script");
      script.src = "https://player.vimeo.com/api/player.js";
      script.onload = () => {
        this.vimeo.callbacks.forEach((c) => c());
      };
      document.head.appendChild(script);
    }
  },
};

class VideoIntersectionInsert extends HTMLElement {
  constructor() {
    super();
    this._observer = null;
    this._isPlaying = false;
    this._loaded = false;
    this._ready = false;
    this._pendingPlay = false;

    this._provider = this.dataset.provider || "html5";
    this._videoId = this.dataset.videoId;
    this._muted = this.dataset.muted === "true";
    this._controls = this.dataset.controls === "true";
    this._player = null;
    this._isModal = this.dataset.isModal === "true";
    this._pausedOnLoad = this.dataset.pausedOnLoad === "true";
    this._loop = this.dataset.loop !== "false";
  }

  connectedCallback() {
    const hasTemplate = this.querySelector("template[data-video-src]");
    if (!this._videoId && !this.dataset.src && !hasTemplate) return;

    // Modal videos load only on explicit playMedia() (user click), not on scroll into view.
    if (this._isModal) return;

    this._observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const isVisible = entry.isIntersecting;

        if (isVisible && !this._loaded) {
          this.loadMedia();
        }

        if (this._pausedOnLoad) {
          if (!isVisible && this._isPlaying) {
            this.pauseMedia();
          }
        } else {
          if (isVisible && this._loaded && !this._isPlaying) {
            if (this._muted || this._isModal) {
              this.playMedia();
            }
          } else if (!isVisible && this._isPlaying) {
            this.handleViewportExit();
          }
        }
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: [0],
      },
    );

    this._observer.observe(this);
  }

  handleViewportExit() {
    this.pauseMedia();
    if (!this._muted) {
      this.muteMedia();
    }
  }

  _clearPlayer() {
    Array.from(this.children).forEach((child) => {
      if (
        child.hasAttribute("data-video-poster") ||
        child.matches("template[data-video-src]")
      ) {
        return;
      }
      child.remove();
    });
    this._player = null;
  }

  _togglePoster(show) {
    const poster = this.querySelector("[data-video-poster]");
    if (!poster) return;
    poster.hidden = !show;
    poster.style.opacity = show ? "1" : "0";
    poster.style.pointerEvents = show ? "auto" : "none";
  }

  _mountHTML5Video() {
    const template = this.querySelector("template[data-video-src]");
    if (template) {
      const fragment = template.content.cloneNode(true);
      this._player = fragment.querySelector("video");
      if (!this._player) return false;
      this._player.classList.add("relative", "z-0", "h-full", "w-full");
      this._player.loop = this._loop;
      if (this._controls) this._player.controls = true;
      this.appendChild(this._player);
      return true;
    }

    if (this.dataset.src) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = this.dataset.src;
      this._player = tempDiv.querySelector("video");
      if (!this._player) return false;
      this._player.classList.add("relative", "z-0", "h-full", "w-full");
      this._player.loop = this._loop;
      this.appendChild(this._player);
      return true;
    }

    if (this._videoId) {
      this._player = document.createElement("video");
      this._player.src = this._videoId;
      this._player.className =
        `${this.dataset.class || ""} relative z-0 h-full w-full`.trim();
      this._player.loop = this._loop;
      this._player.playsInline = true;
      if (this._muted) this._player.muted = true;
      if (this._controls) this._player.controls = true;
      this.appendChild(this._player);
      return true;
    }

    return false;
  }

  loadMedia() {
    this._clearPlayer();
    this._loaded = true;

    if (this._provider === "youtube") {
      const wrap = document.createElement("div");
      wrap.className = "relative z-0 h-full w-full overflow-hidden";
      if (!this._controls) wrap.style.pointerEvents = "none";
      const container = document.createElement("div");
      container.className = "h-full w-full";
      wrap.appendChild(container);
      this.appendChild(wrap);
      APILoader.loadYouTube(() => this.initYouTube(container));
    } else if (this._provider === "vimeo") {
      const wrap = document.createElement("div");
      wrap.className = "relative z-0 h-full w-full overflow-hidden";
      if (!this._controls) wrap.style.pointerEvents = "none";
      const container = document.createElement("div");
      container.className = "h-full w-full";
      wrap.appendChild(container);
      this.appendChild(wrap);
      APILoader.loadVimeo(() => this.initVimeo(container));
    } else {
      this.loadHTML5();
    }
  }

  loadHTML5() {
    if (!this._mountHTML5Video()) return;

    if (!this._controls) {
      this._player.removeAttribute("controls");
      if (this._muted) {
        this._player.style.pointerEvents = "none";
      } else {
        this._player.style.cursor = "pointer";
        this._player.addEventListener("click", () => {
          if (this._isPlaying) {
            this.pauseMedia();
          } else {
            this.playMedia();
          }
        });
      }
    }

    this._player.addEventListener("play", () => {
      this._isPlaying = true;
      this._togglePoster(false);
    });
    this._player.addEventListener("pause", () => {
      this._isPlaying = false;
      if (this._pausedOnLoad && !this._isModal) this._togglePoster(true);
    });

    const startPlayback = () => {
      if (this._isModal && !this._muted && this._player) {
        this._player.muted = true;
      }
      this.playMedia();
    };

    if (this._pendingPlay || (this._muted && !this._pausedOnLoad)) {
      if (this._player.readyState >= 1) {
        startPlayback();
      } else {
        this._player.addEventListener("loadeddata", startPlayback, {
          once: true,
        });
        this._player.load();
      }
    }
  }

  initYouTube(container) {
    const modalAutoplay = this._isModal && this._pendingPlay;
    const autoplayFlag =
      modalAutoplay || (this._muted && !this._pausedOnLoad && !this._isModal)
        ? 1
        : 0;
    const muteFlag = this._muted || this._isModal ? 1 : 0;

    // 动态判断：有控制条就给完整原生体验，无控制条则极限精简
    const playerVars = {
      autoplay: autoplayFlag,
      controls: this._controls ? 1 : 0,
      mute: muteFlag,
      loop: 1,
      playlist: this._videoId,
      playsinline: 1,
      rel: 0,
    };

    if (!this._controls) {
      playerVars.modestbranding = 1;
      playerVars.disablekb = 1;
      playerVars.iv_load_policy = 3;
      playerVars.fs = 0;
      playerVars.cc_load_policy = 0;
    }

    this._player = new YT.Player(container, {
      videoId: this._videoId,
      width: "100%", // 强制满宽满高，修复 UI 控制栏被切除的 Bug
      height: "100%",
      playerVars: playerVars,
      events: {
        onReady: () => {
          this._ready = true;
          if (!this._controls) {
            const iframe = this.querySelector("iframe");
            if (iframe) iframe.style.pointerEvents = "none";
          }
          if (this._pendingPlay) {
            this._pendingPlay = false;
            this.playMedia();
          } else if (!this._pausedOnLoad && (this._muted || this._isModal)) {
            this.playMedia();
          }
        },
        onStateChange: (e) => {
          if (e.data === YT.PlayerState.PLAYING) {
            this._isPlaying = true;
            this._togglePoster(false);
          } else if (e.data === YT.PlayerState.PAUSED) {
            this._isPlaying = false;
            if (this._pausedOnLoad && !this._isModal) this._togglePoster(true);
          }
        },
      },
    });
  }

  initVimeo(container) {
    const modalAutoplay = this._isModal && this._pendingPlay;
    const shouldAutoplay =
      modalAutoplay || (this._muted && !this._pausedOnLoad && !this._isModal);
    const startMuted = this._muted || this._isModal;

    this._player = new Vimeo.Player(container, {
      id: this._videoId,
      width: "100%",
      height: "100%",
      autoplay: shouldAutoplay,
      controls: this._controls,
      muted: startMuted,
      loop: true,
      background:
        !this._controls && startMuted && !this._pausedOnLoad && !modalAutoplay,
      // 动态判断：只有 controls: true 时才显示原生标题和头像信息
      title: this._controls,
      byline: this._controls,
      portrait: this._controls,
      dnt: true,
    });

    this._player.ready().then(() => {
      this._ready = true;
      this._player.getDuration?.().then((duration) => {
        this._cachedDuration = duration || 0;
      });
      if (this._pendingPlay) {
        this._pendingPlay = false;
        this.playMedia();
      } else if (!this._pausedOnLoad && (this._muted || this._isModal)) {
        this.playMedia();
      }
    });

    this._player.on("timeupdate", (data) => {
      this._cachedTime = data?.seconds || 0;
      if (data?.duration) this._cachedDuration = data.duration;
    });

    this._player.on("play", () => {
      this._isPlaying = true;
      this._togglePoster(false);
    });
    this._player.on("pause", () => {
      this._isPlaying = false;
      if (this._pausedOnLoad && !this._isModal) this._togglePoster(true);
    });
  }

  getCurrentTime() {
    if (this._provider === "html5" && this._player) {
      return this._player.currentTime || 0;
    }
    if (this._provider === "youtube" && this._player?.getCurrentTime) {
      try {
        return this._player.getCurrentTime() || 0;
      } catch (error) {
        return 0;
      }
    }
    if (this._provider === "vimeo") return this._cachedTime || 0;
    return 0;
  }

  getDuration() {
    if (this._provider === "html5" && this._player) {
      return this._player.duration || 0;
    }
    if (this._provider === "youtube" && this._player?.getDuration) {
      try {
        return this._player.getDuration() || 0;
      } catch (error) {
        return 0;
      }
    }
    if (this._provider === "vimeo") return this._cachedDuration || 0;
    return 0;
  }

  seekTo(seconds) {
    const time = Math.max(0, Number(seconds) || 0);
    if (this._provider === "html5" && this._player) {
      this._player.currentTime = time;
    } else if (this._provider === "youtube" && this._player?.seekTo) {
      this._player.seekTo(time, true);
    } else if (this._provider === "vimeo" && this._player?.setCurrentTime) {
      this._cachedTime = time;
      this._player.setCurrentTime(time);
    }
  }

  playMedia() {
    if (!this._loaded) {
      this._pendingPlay = true;
      this.loadMedia();
      return;
    }

    if (
      (this._provider === "youtube" || this._provider === "vimeo") &&
      !this._ready
    ) {
      this._pendingPlay = true;
      return;
    }

    if (!this._muted) {
      if (
        GlobalAudioContext.activeElement &&
        GlobalAudioContext.activeElement !== this
      ) {
        GlobalAudioContext.activeElement.muteMedia();
      }
      GlobalAudioContext.activeElement = this;
    }

    if (this._provider === "html5" && this._player) {
      if (this._isModal && !this._muted) {
        this._player.muted = true;
      }

      const playPromise = this._player.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            this._isPlaying = true;
            this._togglePoster(false);
            if (!this._muted) this._player.muted = false;
          })
          .catch(() => {
            this._isPlaying = false;
          });
      }
    } else if (this._provider === "youtube" && this._player?.playVideo) {
      if (this._isModal && !this._muted) {
        this._player.mute();
      }
      this._player.playVideo();
      if (!this._muted) {
        this._player.unMute();
      }
      this._isPlaying = true;
    } else if (this._provider === "vimeo" && this._player?.play) {
      const playPromise = this._player.play();
      if (!this._muted) {
        playPromise?.then?.(() => this._player.setVolume(1));
      }
      this._isPlaying = true;
    }
  }

  pauseMedia() {
    this._pendingPlay = false;

    if (this._provider === "html5" && this._player) {
      this._player.pause();
    } else if (this._provider === "youtube" && this._player?.pauseVideo) {
      this._player.pauseVideo();
    } else if (this._provider === "vimeo" && this._player?.pause) {
      this._player.pause();
    }
    this._isPlaying = false;
    if (this._pausedOnLoad && !this._isModal) this._togglePoster(true);
  }

  muteMedia() {
    if (this._provider === "html5" && this._player) {
      this._player.muted = true;
    } else if (this._provider === "youtube" && this._player?.mute) {
      this._player.mute();
    } else if (this._provider === "vimeo" && this._player?.setVolume) {
      this._player.setVolume(0);
    }

    if (GlobalAudioContext.activeElement === this) {
      GlobalAudioContext.activeElement = null;
    }
  }

  resetMedia() {
    if (this._provider === "html5" && this._player) {
      this._player.currentTime = 0;
    } else if (this._provider === "youtube" && this._player?.seekTo) {
      this._player.seekTo(0, true);
    } else if (this._provider === "vimeo" && this._player?.setCurrentTime) {
      this._player.setCurrentTime(0);
    }
  }

  resetPlayer() {
    this.pauseMedia();
    this._clearPlayer();
    this._ready = false;
    this._loaded = false;
    this._pendingPlay = false;
    this._togglePoster(true);
  }

  disconnectedCallback() {
    this._observer?.disconnect();
  }
}

if (!customElements.get("video-intersection-insert")) {
  customElements.define("video-intersection-insert", VideoIntersectionInsert);
}

class productMediaSwiper extends HTMLElement {
  connectedCallback() {
    this.initSwiper();
  }
  initSwiper() {
    const swiperEl = this.querySelector(".swiper");
    new Swiper(swiperEl, {
      observer: true,
      observeParents: true,
      slidesPerView: 1,
      spaceBetween: 8,
      loop: true,
      loopAdditionalSlides: 3,
      on: {
        slideChange(swiperInstance) {
          const realIndex = swiperInstance.realIndex;
          console.log("Slide changed to index:", realIndex);
        },
      },
      breakpoints: {
        768: {
          centeredSlides: true,
          spaceBetween: 10,
        },
        1000: {
          centeredSlides: true,
          spaceBetween: 12,
        },
      },
      pagination: {
        el: this.querySelector(".pagination"),
        clickable: true,
        renderBullet: function (index, className) {
          const bulletClasses = `
              ${className}
              rounded-none
              w-8 h-8 bg-[#CCCCCC] transition-all duration-300 block cursor-pointer
              hover:bg-white
              [&.swiper-pagination-bullet-active]:w-50 [&.swiper-pagination-bullet-active]:h-8 [&.swiper-pagination-bullet-active]:bg-[#FEDB1E]
            `;
          return `<span class="${bulletClasses.trim().replace(/\s+/g, " ")}"></span>`;
        },
      },
      navigation: {
        nextEl: this.querySelector(".swiper-button-next"),
        prevEl: this.querySelector(".swiper-button-prev"),
      },
    });
  }
}
if (!customElements.get("product-media-swiper")) {
  customElements.define("product-media-swiper", productMediaSwiper);
}

class SaProductCard extends HTMLElement {
  connectedCallback() {
    this.bindInteractions();
  }

  get isInProductsSwiper() {
    return Boolean(this.closest(".sa-products-swiper"));
  }

  getProductSlide() {
    return this.closest(".sa-products-swiper .swiper-slide");
  }

  getParentSwiper() {
    const swiperEl = this.closest(".sa-products-swiper");
    return swiperEl?.swiper ?? null;
  }

  get mediaEl() {
    return this.querySelector(".sa-product-card-media");
  }

  get hasSecondaryImage() {
    return Boolean(this.querySelector(".sa-product-card-image--secondary"));
  }

  elevateSlide(active) {
    if (!this.isInProductsSwiper) return;

    const slide = this.getProductSlide();
    if (slide) slide.style.zIndex = active ? "2" : "";
  }

  showImage(index) {
    const showSecondary = index === 1;
    this.setPreview(showSecondary);
    this.elevateSlide(showSecondary);
  }

  setPreview(active) {
    this.classList.toggle("is-preview", active);
  }

  bindInteractions() {
    if (this._interactionsBound) return;

    this._interactionsBound = true;
    this._hoverMq = window.matchMedia("(hover: hover) and (pointer: fine)");
    this._mediaSwiped = false;
    this._showPreview = () => this.setPreview(true);
    this._hidePreview = () => this.setPreview(false);

    const media = this.mediaEl;

    // Desktop: only the image area switches to the second image on hover
    this._onEnter = () => {
      if (!this._hoverMq.matches || !this.hasSecondaryImage) return;
      this.elevateSlide(true);
      this._showPreview();
    };

    this._onLeave = () => {
      if (!this._hoverMq.matches) return;
      this.elevateSlide(false);
      this._hidePreview();
    };

    media?.addEventListener("mouseenter", this._onEnter);
    media?.addEventListener("mouseleave", this._onLeave);

    this.querySelectorAll("[data-product-card-dot]").forEach((dot) => {
      dot.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.showImage(Number(dot.dataset.productCardDot));
      });
    });

    this.bindMediaTouch();

    this.querySelectorAll("[data-sa-product-card-link]").forEach((link) => {
      let pointerStartX = 0;
      let pointerMoved = false;

      link.addEventListener("pointerdown", (event) => {
        pointerStartX = event.clientX;
        pointerMoved = false;
        this._mediaSwiped = false;
      });

      link.addEventListener("pointermove", (event) => {
        if (Math.abs(event.clientX - pointerStartX) > 8) {
          pointerMoved = true;
        }
      });

      link.addEventListener("click", (event) => {
        if (pointerMoved || this._mediaSwiped) {
          event.preventDefault();
          return;
        }

        const href = link.dataset.saProductCardLink;
        if (href) window.location.href = href;
      });

      link.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        const href = link.dataset.saProductCardLink;
        if (href) window.location.href = href;
      });
    });

    this._syncHover = () => {
      if (!this._hoverMq.matches) this._hidePreview();
    };

    this._hoverMq.addEventListener("change", this._syncHover);
  }

  bindMediaTouch() {
    const media = this.mediaEl;
    if (!media || !this.hasSecondaryImage) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;
    let moved = false;
    let axis = null; // 'x' | 'y' | null
    let parentSwiperLocked = false;

    const unlockParentSwiper = () => {
      if (!parentSwiperLocked) return;
      const swiper = this.getParentSwiper();
      if (swiper) swiper.allowTouchMove = true;
      parentSwiperLocked = false;
    };

    const lockParentSwiper = () => {
      const swiper = this.getParentSwiper();
      if (!swiper || parentSwiperLocked) return;
      swiper.allowTouchMove = false;
      parentSwiperLocked = true;
    };

    this._onMediaPointerDown = (event) => {
      if (this._hoverMq.matches) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;

      startX = event.clientX;
      startY = event.clientY;
      tracking = true;
      moved = false;
      axis = null;
      this._mediaSwiped = false;

      // Lock parent carousel immediately so image swipe isn't stolen
      lockParentSwiper();

      try {
        media.setPointerCapture(event.pointerId);
      } catch (_) {
        /* ignore */
      }
    };

    this._onMediaPointerMove = (event) => {
      if (!tracking || this._hoverMq.matches) return;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      if (!moved && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        moved = true;
      }

      // Decide axis once, then keep parent locked only for horizontal image swipes
      if (!axis && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
        if (axis === "y") {
          // Vertical scroll — give parent/page back control
          unlockParentSwiper();
        } else {
          lockParentSwiper();
          event.preventDefault();
          event.stopPropagation();
        }
      } else if (axis === "x") {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    this._onMediaPointerUp = (event) => {
      if (!tracking || this._hoverMq.matches) return;
      tracking = false;

      try {
        if (media.hasPointerCapture?.(event.pointerId)) {
          media.releasePointerCapture(event.pointerId);
        }
      } catch (_) {
        /* ignore */
      }

      unlockParentSwiper();

      // Ignore taps on pagination dots — they have their own handlers
      if (event.target?.closest?.("[data-product-card-dot]")) return;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      const threshold = 40;

      // Horizontal swipe → switch image
      if (Math.abs(dx) >= threshold && Math.abs(dx) > Math.abs(dy)) {
        this._mediaSwiped = true;
        this.showImage(dx < 0 ? 1 : 0);
        return;
      }

      // Tap → toggle second image (block link navigation for this gesture)
      if (!moved) {
        this._mediaSwiped = true;
        const showingSecondary = this.classList.contains("is-preview");
        this.showImage(showingSecondary ? 0 : 1);
      }
    };

    this._onMediaPointerCancel = (event) => {
      tracking = false;
      unlockParentSwiper();
      try {
        if (
          event?.pointerId != null &&
          media.hasPointerCapture?.(event.pointerId)
        ) {
          media.releasePointerCapture(event.pointerId);
        }
      } catch (_) {
        /* ignore */
      }
    };

    media.addEventListener("pointerdown", this._onMediaPointerDown);
    media.addEventListener("pointermove", this._onMediaPointerMove, {
      passive: false,
    });
    media.addEventListener("pointerup", this._onMediaPointerUp);
    media.addEventListener("pointercancel", this._onMediaPointerCancel);
  }

  disconnectedCallback() {
    if (!this._interactionsBound) return;

    const media = this.mediaEl;
    media?.removeEventListener("mouseenter", this._onEnter);
    media?.removeEventListener("mouseleave", this._onLeave);
    this._hoverMq?.removeEventListener("change", this._syncHover);

    if (media && this._onMediaPointerDown) {
      media.removeEventListener("pointerdown", this._onMediaPointerDown);
      media.removeEventListener("pointermove", this._onMediaPointerMove);
      media.removeEventListener("pointerup", this._onMediaPointerUp);
      media.removeEventListener("pointercancel", this._onMediaPointerCancel);
    }

    this.elevateSlide(false);
    this._interactionsBound = false;
  }
}

if (!customElements.get("sa-product-card")) {
  customElements.define("sa-product-card", SaProductCard);
}

function applySaCatalogCardVariant(card, data) {
  if (!card || !data) return;

  const image = card.querySelector(".sa-product-card__media");
  if (image && data.image) {
    image.src = data.image;
    if (data.srcset) image.srcset = data.srcset;
  }

  card.querySelectorAll("[data-sa-card-link]").forEach((link) => {
    if (data.url) link.setAttribute("href", data.url);
  });

  const price = card.querySelector("[data-sa-card-price]");
  if (price && data.price) {
    const template = document.createElement("template");
    template.innerHTML = data.price;
    price.textContent = (template.content.textContent || data.price).trim();
  }

  const saleBadge = card.querySelector("[data-sa-card-sale-badge]");
  if (saleBadge) saleBadge.classList.toggle("is-hidden", !data.sale);

  const soldBadge = card.querySelector("[data-sa-card-sold-badge]");
  if (soldBadge) soldBadge.classList.toggle("is-hidden", !data.sold);

  const cart = card.querySelector("[data-sa-card-cart]");
  const cartButton = card.querySelector("[data-sa-card-cart-button]");
  if (cart && data.variantId) cart.setAttribute("data-variant-id", data.variantId);
  if (cartButton) {
    if (data.available) cartButton.removeAttribute("disabled");
    else cartButton.setAttribute("disabled", "");
  }
}

function onSaCatalogCardSwatchClick(event) {
  const swatch = event.target.closest("[data-sa-card-swatch]");
  if (!swatch) return;

  const card = swatch.closest("sa-catalog-card");
  if (!card || !card.contains(swatch)) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  event.preventDefault();
  event.stopPropagation();

  let data;
  try {
    data = JSON.parse(swatch.getAttribute("data-sa-card-swatch"));
  } catch (error) {
    return;
  }

  applySaCatalogCardVariant(card, data);
  card.querySelectorAll("[data-sa-card-swatch]").forEach((item) => {
    item.removeAttribute("aria-current");
  });
  swatch.setAttribute("aria-current", "page");
  swatch.blur();
}

if (!window.__saCatalogCardSwatchBound) {
  window.__saCatalogCardSwatchBound = true;
  document.addEventListener("click", onSaCatalogCardSwatchClick, true);
}

class SaCatalogCard extends HTMLElement {
  applyVariant(data) {
    applySaCatalogCardVariant(this, data);
  }
}

if (!customElements.get("sa-catalog-card")) {
  customElements.define("sa-catalog-card", SaCatalogCard);
}

var RecentlyViewedProducts = class extends HTMLElement {
  async connectedCallback() {
    if (this.searchQueryString === "") {
      return;
    }
    const response = await fetch(
      `/search?type=product&q=${this.searchQueryString}&section_id=${this.sectionId}`,
    );
    const div = document.createElement("div");
    div.innerHTML = await response.text();
    const recentlyViewedProductsElement = div.querySelector(
      "recently-viewed-products",
    );
    if (recentlyViewedProductsElement.hasChildNodes()) {
      this.innerHTML = recentlyViewedProductsElement.innerHTML;
    } else {
      this.innerHTML = "";
    }
  }
  get searchQueryString() {
    const items = JSON.parse(
      localStorage.getItem("theme:recently-viewed-products") || "[]",
    );
    if (
      this.hasAttribute("exclude-product-id") &&
      items.includes(parseInt(this.getAttribute("exclude-product-id")))
    ) {
      items.splice(
        items.indexOf(parseInt(this.getAttribute("exclude-product-id"))),
        1,
      );
    }
    return items
      .map((item) => "id:" + item)
      .slice(0, this.productsCount)
      .join(" OR ");
  }
  get sectionId() {
    return this.getAttribute("section-id");
  }
  get productsCount() {
    return this.getAttribute("products-count") || 4;
  }
};
if (!customElements.get("recently-viewed-products")) {
  customElements.define("recently-viewed-products", RecentlyViewedProducts);
}

var SaCompareData = class extends HTMLElement {
  async load(ids) {
    this._ids = (ids || [])
      .map((id) => parseInt(id, 10))
      .filter((id) => Number.isFinite(id));
    if (this.searchQueryString === "") {
      this.innerHTML = "";
      return;
    }
    if (this._abort) this._abort.abort();
    this._abort = new AbortController();
    const response = await fetch(
      `${this.searchUrl}?type=product&q=${this.searchQueryString}&section_id=${this.sectionId}`,
      { signal: this._abort.signal },
    );
    const div = document.createElement("div");
    div.innerHTML = await response.text();
    const compareDataElement = div.querySelector("sa-compare-data");
    if (compareDataElement && compareDataElement.hasChildNodes()) {
      this.innerHTML = compareDataElement.innerHTML;
    } else {
      this.innerHTML = "";
    }
  }
  get searchUrl() {
    return (
      window.themeVariables?.routes?.searchUrl ||
      window.routes?.search_url ||
      "/search"
    );
  }
  get searchQueryString() {
    const items = this._ids || [];
    return items
      .map((item) => "id:" + item)
      .slice(0, this.productsCount)
      .join(" OR ");
  }
  get sectionId() {
    return this.getAttribute("section-id");
  }
  get productsCount() {
    return this.getAttribute("products-count") || 3;
  }
};
if (!customElements.get("sa-compare-data")) {
  customElements.define("sa-compare-data", SaCompareData);
}

class ArticleToc extends HTMLElement {
  connectedCallback() {
    const contentSel =
      this.getAttribute("content-selector") || ".article-template__content";
    const titleSel = this.getAttribute("title-selector") || "h2";
    const content = document.querySelector(contentSel);
    if (!content) return;

    const headings = Array.from(content.querySelectorAll(titleSel));
    if (headings.length === 0) {
      this.hidden = true;
      return;
    }

    // Shuffle and take up to 6
    const shuffled = headings
      .slice()
      .sort(() => Math.random() - 0.5)
      .slice(0, 6);
    // Re-sort by DOM order so numbering stays logical
    shuffled.sort((a, b) => (a.compareDocumentPosition(b) & 4 ? -1 : 1));

    // Ensure each heading has an id for anchor linking
    shuffled.forEach((h, i) => {
      if (!h.id) {
        h.id =
          "toc-heading-" +
          i +
          "-" +
          h.textContent
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^a-z0-9-]/g, "")
            .slice(0, 48);
      }
    });

    const links = shuffled
      .map(
        (h, i) =>
          `<a href="#${h.id}" class="text-[14px] md:text-[15px] hover:opacity-60 transition-opacity">${i + 1} - <span class="underline">${h.textContent.trim()}</span></a>`,
      )
      .join("");

    this.innerHTML = links;
  }
}
if (!customElements.get("article-toc")) {
  customElements.define("article-toc", ArticleToc);
}

window.SaHScrollTabs = {
  MOBILE_MQ: "(max-width: 767px)",

  scrollActiveTab(tabList, tab, smooth = true) {
    if (!tabList || !tab || !window.matchMedia(this.MOBILE_MQ).matches) return;

    const maxScroll = tabList.scrollWidth - tabList.clientWidth;
    if (maxScroll <= 0) return;

    const target = tab.offsetLeft - (tabList.clientWidth - tab.offsetWidth) / 2;

    tabList.scrollTo({
      left: Math.max(0, Math.min(target, maxScroll)),
      behavior: smooth ? "smooth" : "auto",
    });
  },
};

function getAnchorHeaderOffset() {
  const header =
    document.querySelector("[data-sa-header]") ||
    document.querySelector("store-header");
  if (header) {
    return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
  }

  const cssOffset = getComputedStyle(document.documentElement)
    .getPropertyValue("--sa-header-bottom-offset")
    .trim();
  const parsed = parseInt(cssOffset, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function initAnchorScrollLinks() {
  const findAnchorTarget = (targetId) => {
    if (!targetId) return null;
    const exact = document.querySelector(
      `[data-anchor-target-id='${CSS.escape(targetId)}']`,
    );
    if (exact) return exact;

    const needle = targetId.toLowerCase();
    return (
      Array.from(document.querySelectorAll("[data-anchor-target-id]")).find(
        (el) =>
          (el.getAttribute("data-anchor-target-id") || "").toLowerCase() ===
          needle,
      ) ||
      document.getElementById(targetId) ||
      null
    );
  };

  document.querySelectorAll("a[data-anchor-id]").forEach((link) => {
    if (link.dataset.anchorScrollBound === "true") return;

    link.dataset.anchorScrollBound = "true";
    link.addEventListener("click", (e) => {
      const targetId = link.dataset.anchorId;
      if (!targetId) return;

      const target = findAnchorTarget(targetId);
      if (!target) return;

      e.preventDefault();

      const anchors = document.querySelector(".sa-anchors");
      const headerOffset = getAnchorHeaderOffset();
      const anchorsOffset = anchors ? anchors.offsetHeight : 0;
      const targetTop =
        target.getBoundingClientRect().top +
        window.scrollY -
        headerOffset -
        anchorsOffset;
      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: "auto",
      });
    });
  });
}

window.initAnchorScrollLinks = initAnchorScrollLinks;

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAnchorScrollLinks);
} else {
  initAnchorScrollLinks();
}
document.addEventListener("shopify:section:load", initAnchorScrollLinks);
const SA_WISHLIST_KEY = "theme:sa-wishlist";

function readSaWishlistIds() {
  try {
    const raw = JSON.parse(localStorage.getItem(SA_WISHLIST_KEY) || "[]");
    if (!Array.isArray(raw)) return [];
    return raw
      .map((id) => parseInt(id, 10))
      .filter((id) => Number.isFinite(id));
  } catch (error) {
    return [];
  }
}

function writeSaWishlistIds(ids, silent) {
  const unique = [];
  ids.forEach((id) => {
    const parsed = parseInt(id, 10);
    if (!Number.isFinite(parsed) || unique.includes(parsed)) return;
    unique.push(parsed);
  });
  localStorage.setItem(SA_WISHLIST_KEY, JSON.stringify(unique.slice(0, 50)));
  if (!silent) {
    document.dispatchEvent(
      new CustomEvent("sa:wishlist:change", { detail: { ids: unique } }),
    );
  }
  return unique;
}

function toggleSaWishlistId(id) {
  const parsed = parseInt(id, 10);
  if (!Number.isFinite(parsed)) return false;
  const ids = readSaWishlistIds();
  const index = ids.indexOf(parsed);
  if (index === -1) ids.unshift(parsed);
  else ids.splice(index, 1);
  writeSaWishlistIds(ids);
  return index === -1;
}

function syncSaWishlistButtons(root) {
  const scope = root || document;
  const ids = readSaWishlistIds();
  scope.querySelectorAll("[data-sa-card-wishlist]").forEach((button) => {
    const id = parseInt(button.getAttribute("data-pid"), 10);
    if (!Number.isFinite(id)) return;
    const active = ids.includes(id);
    button.setAttribute("aria-pressed", active ? "true" : "false");
    const label = active
      ? button.getAttribute("data-remove-label")
      : button.getAttribute("data-add-label");
    if (label) button.setAttribute("aria-label", label);
  });
}

function onSaWishlistClick(event) {
  const button = event.target.closest("[data-sa-card-wishlist]");
  if (!button || button.disabled) return;
  const id = parseInt(button.getAttribute("data-pid"), 10);
  if (!Number.isFinite(id)) return;
  event.preventDefault();
  toggleSaWishlistId(id);
}

class SaWishlist extends HTMLElement {
  connectedCallback() {
    this._onChange = (event) => this.syncFromStorage(event?.detail?.ids);
    document.addEventListener("sa:wishlist:change", this._onChange);
    this.refresh();
  }

  disconnectedCallback() {
    document.removeEventListener("sa:wishlist:change", this._onChange);
  }

  get searchUrl() {
    return window.routes?.search_url || "/search";
  }

  get sectionId() {
    return this.getAttribute("section-id");
  }

  get productsCount() {
    return parseInt(this.getAttribute("products-count"), 10) || 50;
  }

  get grid() {
    return this.querySelector("[data-sa-wishlist-grid]");
  }

  get emptyEl() {
    return this.querySelector("[data-sa-wishlist-empty]");
  }

  get loadingEl() {
    return this.querySelector("[data-sa-wishlist-loading]");
  }

  get countEl() {
    return this.querySelector("[data-sa-wishlist-count]");
  }

  setLoading(isLoading) {
    if (!this.loadingEl) return;
    this.loadingEl.hidden = !isLoading;
  }

  setEmpty(isEmpty) {
    if (this.emptyEl) this.emptyEl.hidden = !isEmpty;
    if (this.grid) this.grid.hidden = isEmpty;
  }

  updateCount(count) {
    if (!this.countEl) return;
    this.countEl.textContent = count > 0 ? ` (${count})` : "";
  }

  syncFromStorage(ids) {
    const current = ids || readSaWishlistIds();
    const items = this.querySelectorAll("[data-sa-wishlist-item]");
    if (items.length === 0 && current.length > 0 && !this._loadedOnce) {
      this.refresh();
      return;
    }
    items.forEach((item) => {
      const id = parseInt(item.getAttribute("data-pid"), 10);
      if (!current.includes(id)) item.remove();
    });
    const remaining = this.querySelectorAll("[data-sa-wishlist-item]").length;
    this.updateCount(remaining);
    this.setEmpty(remaining === 0);
    if (remaining === 0) this._loadedOnce = false;
  }

  async refresh() {
    if (this.hasAttribute("data-design-mode")) return;
    const ids = readSaWishlistIds().slice(0, this.productsCount);
    this.updateCount(ids.length);
    if (ids.length === 0) {
      this.setLoading(false);
      this.setEmpty(true);
      if (this.grid) this.grid.innerHTML = "";
      this._loadedOnce = true;
      return;
    }

    this.setEmpty(false);
    this.setLoading(true);
    const query = ids.map((id) => "id:" + id).join(" OR ");
    try {
      if (this._abort) this._abort.abort();
      this._abort = new AbortController();
      const response = await fetch(
        `${this.searchUrl}?type=product&q=${encodeURIComponent(query)}&section_id=${this.sectionId}`,
        { signal: this._abort.signal },
      );
      const html = await response.text();
      const div = document.createElement("div");
      div.innerHTML = html;
      const next = div.querySelector("sa-wishlist");
      const nextGrid = next?.querySelector("[data-sa-wishlist-grid]");
      if (this.grid && nextGrid) {
        this.grid.innerHTML = nextGrid.innerHTML;
      }
      const found = Array.from(
        this.querySelectorAll("[data-sa-wishlist-item]"),
      ).map((item) => parseInt(item.getAttribute("data-pid"), 10));
      const pruned = ids.filter((id) => found.includes(id));
      if (pruned.length !== ids.length) {
        writeSaWishlistIds(pruned, true);
      }
      this.updateCount(pruned.length);
      this.setEmpty(pruned.length === 0);
      this._loadedOnce = true;
      syncSaWishlistButtons(this);
    } catch (error) {
      if (error?.name !== "AbortError") {
        this.setEmpty(true);
      }
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get("sa-wishlist")) {
  customElements.define("sa-wishlist", SaWishlist);
}

if (!window.__saWishlistBound) {
  window.__saWishlistBound = true;
  document.addEventListener("click", onSaWishlistClick);
  document.addEventListener("sa:wishlist:change", () => {
    syncSaWishlistButtons();
  });
  const bootWishlist = () => syncSaWishlistButtons();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootWishlist);
  } else {
    bootWishlist();
  }
  document.addEventListener("shopify:section:load", (event) => {
    syncSaWishlistButtons(event.target);
  });
}
})();
